import { useEffect, useRef, ReactNode, MouseEvent } from 'react';
import { Capacitor } from '@capacitor/core';

import style from './style.module.css';
import { setStatusBarStyle, setSafeAreaBackgroundColor } from '../../utilities/statusBar';

interface CustomImage {
    Image?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    className?: string;
}

interface WindowProps {
    children?: ReactNode;
    isOpened?: boolean;
    customImage: CustomImage;
    statusBarColor?: string; // Couleur de fond pour la barre de statut (défaut: blanc)
    darkStatusBarText?: boolean; // Si true, texte sombre sur fond clair (défaut: false = texte clair)
}

const Window = ({ children, customImage, isOpened = false, statusBarColor = '#FFFFFF', darkStatusBarText = false }: WindowProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const isMobile = Capacitor.isNativePlatform();
    // const mouseDownTarget = useRef<EventTarget | null>(null);

    useEffect(() => {
        document.body.style.overflow = isOpened ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpened]);

    // Gérer la couleur de la barre de statut sur mobile
    useEffect(() => {
        if (!isMobile) return;

        if (isOpened) {
            // Appliquer la couleur de la fenêtre
            setSafeAreaBackgroundColor(statusBarColor);
            setStatusBarStyle(!darkStatusBarText); // isDarkBackground inversé car darkStatusBarText indique le texte
        } else {
            // Restaurer la couleur par défaut (transparent ou couleur de l'app)
            setSafeAreaBackgroundColor('transparent');
            // Vous pouvez ajuster le style par défaut selon votre app
            setStatusBarStyle(true); // Ajustez selon votre thème par défaut
        }
    }, [isOpened, isMobile, statusBarColor, darkStatusBarText]);

    return (
        <div
            className={`${style.bgd} ${isOpened ? style.opened : ''}`}
            // Uncomment the following lines to close the window when clicking outside of it
            /*onMouseUp={(event) => {
                if (
                    mouseDownTarget.current &&
                    ref.current &&
                    !ref.current.contains(mouseDownTarget.current as Node) &&
                    !ref.current.contains(event.target as Node)
                ) {
                    onClose(event);
                }
            }}
            onMouseDown={(event) => {
                mouseDownTarget.current = event.target;
            }}*/
        >
            <div className={style.window} ref={ref}>
                <div className={style.content}>{children}</div>
            </div>
            {customImage?.Image && <customImage.Image className={[style.custom, customImage.className].join(' ')} />}
        </div>
    );
};

export default Window;